/**
 * Reporting Service — Data adapter for Azure SQL reporting tables.
 *
 * Azure SQL tables:
 *   - projects           → Project Summary Dashboard
 *   - pmh_change_orders  → Change Order History
 *   - actuals            → Spend Summary by Vendor
 *
 * ⚠️  DB credentials must NEVER be committed or used client-side.
 *     All real queries route through the backend API.
 */

import { apiGet } from "./apiClient";

// ─── Types ───────────────────────────────────────────────────────────
export interface ProjectSummary {
  project_name: string;
  client_name: string;
  orig_budget: number;
  owner_changes: number;
  builder_changes: number;
  current_budget: number;
  rem_to_bill: number;
  rem_contingency: number;
  status: string;
}

export interface ChangeOrder {
  project_name: string;
  co_number: string;
  draw_date: string;
  cost_code: string;
  cost_code_desc: string;
  owner_change: number;
  builder_change: number;
  total_change: number;
}

export interface Bill {
  vendor_name: string;
  txn_date: string;
  amount: number;
  is_paid: boolean;
  memo: string;
}

export interface VendorSpendSummary {
  vendor_name: string;
  total_spent: number;
  bill_count: number;
}

// ─── Service functions ───────────────────────────────────────────────

export async function fetchProjectSummaryData(
  projectName?: string
): Promise<ProjectSummary> {
  const params = projectName ? { projectName } : undefined;
  return apiGet({ endpoint: "/reporting/project-summary", params, withAuth: true });
}

export async function fetchChangeOrdersData(
  projectName?: string
): Promise<ChangeOrder[]> {
  const params = projectName ? { projectName } : undefined;
  return apiGet({ endpoint: "/reporting/change-orders", params, withAuth: true });
}

export async function fetchSpendSummaryData(
  projectName?: string
): Promise<VendorSpendSummary[]> {
  const params = projectName ? { projectName } : undefined;
  return apiGet({ endpoint: "/reporting/spend-summary", params, withAuth: true });
}
